import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { Recipe } from '../api/Recipe';
import { useAuth } from './AuthContext';
import {
  readAuthorFavoritesCache,
} from '../domains/favorites/cache';
import {
  initializeFavoritesForAuthor,
  refreshFavoritesForAuthor,
} from '../domains/favorites/use-cases';
import useFavoriteRecipeRealtime from '../hooks/favorites/useFavoriteRecipeRealtime';
import useFavoriteRowsRealtime from '../hooks/favorites/useFavoriteRowsRealtime';
import useFavoriteCacheState from '../hooks/favorites/useFavoriteCacheState';
import useFavoriteMutations from '../hooks/favorites/useFavoriteMutations';

interface FavoritesContextType {
  favorites: Recipe[];
  favoritesLoading: boolean;
  favoritePendingIds: string[];
  addFavorite: (recipe: Recipe) => Promise<void>;
  removeFavorite: (recipeId: string) => Promise<void>;
  isFavorite: (recipeId: string) => boolean;
  refreshFavorites: () => Promise<void>;
}

export const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { author } = useAuth();
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritePendingIds, setFavoritePendingIds] = useState<string[]>([]);
  const {
    favorites,
    favoritesRef,
    setCachedFavorites,
    clearFavorites,
  } = useFavoriteCacheState(author?.id);

  const refreshFavorites = useCallback(async () => {
    await refreshFavoritesForAuthor({
      authorId: author?.id,
      setFavorites: setCachedFavorites,
      clearFavorites,
    });
  }, [author?.id, clearFavorites, setCachedFavorites]);

  useEffect(() => {
    let isCancelled = false;

    if (!author?.id) {
      clearFavorites();
      setFavoritePendingIds([]);
      setFavoritesLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    const cachedFavorites = readAuthorFavoritesCache(author.id);
    if (cachedFavorites.length > 0) {
      setCachedFavorites(cachedFavorites);
    } else {
      clearFavorites();
    }

    const initializeFavorites = async () => {
      setFavoritesLoading(true);
      try {
        await initializeFavoritesForAuthor({
          authorId: author.id,
          isCancelled: () => isCancelled,
          setFavorites: setCachedFavorites,
        });
      } catch (error) {
        console.error('Failed to initialize favorites:', error);
      } finally {
        if (!isCancelled) {
          setFavoritesLoading(false);
        }
      }
    };

    void initializeFavorites();

    return () => {
      isCancelled = true;
    };
  }, [author?.id, clearFavorites, setCachedFavorites]);

  useFavoriteRecipeRealtime({
    favoritesRef,
    setCachedFavorites,
  });

  useFavoriteRowsRealtime({
    authorId: author?.id,
    refreshFavorites,
  });

  const { addFavorite, removeFavorite } = useFavoriteMutations({
    authorId: author?.id,
    favoritesRef,
    setCachedFavorites,
    setFavoritePendingIds,
  });

  const isFavorite = useCallback((recipeId: string) => {
    return favorites.some((favorite) => favorite.id === recipeId);
  }, [favorites]);

  const value = useMemo(() => ({
    favorites,
    favoritesLoading,
    favoritePendingIds,
    addFavorite,
    removeFavorite,
    isFavorite,
    refreshFavorites,
  }), [
    addFavorite,
    favoritePendingIds,
    favorites,
    favoritesLoading,
    isFavorite,
    refreshFavorites,
    removeFavorite,
  ]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = React.useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
